import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_THEME_KEY, THEMES, THEME_STORAGE_KEY, getTheme } from "../theme";

const readStoredThemeKey = () => {
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        return THEMES[stored] ? stored : DEFAULT_THEME_KEY;
    } catch {
        return DEFAULT_THEME_KEY;
    }
};

export const ThemeContext = createContext({
    themeKey: DEFAULT_THEME_KEY,
    theme: getTheme(DEFAULT_THEME_KEY),
    setThemeKey: () => {},
});

export const ThemeProvider = ({ children }) => {
    const [themeKey, setThemeKeyState] = useState(readStoredThemeKey);

    const setThemeKey = useCallback((key) => {
        if (!THEMES[key]) return;
        setThemeKeyState(key);
        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, key);
        } catch {
            // Private mode or blocked storage: the choice still applies for this visit.
        }
    }, []);

    // styles.css switches its variables on this attribute.
    useEffect(() => {
        document.documentElement.dataset.appTheme = themeKey;
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
            themeColorMeta.setAttribute("content", themeKey === "glass" ? "#f2f6f5" : themeKey === "control" ? "#0e151c" : "#151517");
        }
    }, [themeKey]);

    const value = useMemo(
        () => ({ themeKey, theme: getTheme(themeKey), setThemeKey }),
        [themeKey, setThemeKey]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useAppTheme = () => useContext(ThemeContext);
